/**
 * CategoricalDiagram — small inline-SVG primitives for category-theory pictures.
 *
 * These primitives draw labeled object nodes, single arrows, double arrows
 * (natural transformations), commutative squares, adjunction triangles, and
 * F-(co)algebra arrows. They are intentionally minimal and have no external
 * dependencies — every CSK explainer composes the same handful of pieces so
 * the visual grammar stays consistent.
 *
 * Conventions (mirrors `docs/visual/visual-guide/VISUAL-GRAMMAR.md`):
 *
 * - Solid arrow: an allowed morphism.
 * - Dashed arrow: a candidate morphism whose existence is the question.
 * - Double arrow with the marker `║`: a 2-cell (natural transformation).
 * - "Forbidden" arrow: drawn dashed and stamped with `✗`.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { FONT, GRAY } from "../../../design-system/tokens.js";
import { ArrowheadMarker } from "../../../components/ArrowheadMarker.js";

const STROKE = GRAY[700];
const SUBTLE = GRAY[400];
const LABEL = GRAY[900];

/** Canonical monospace font stack for SVG diagram labels. */
export const DIAGRAM_MONO = FONT.mono;

export { STROKE, SUBTLE, LABEL };

/** Standard arrowhead marker definitions; each diagram includes them once. */
export function ArrowDefs({
  id,
  color = STROKE,
}: {
  readonly id: string;
  readonly color?: string;
}): React.JSX.Element {
  return (
    <defs>
      <ArrowheadMarker id={`${id}-head`} color={color} />
    </defs>
  );
}

export interface ObjectNodeProps {
  readonly cx: number;
  readonly cy: number;
  readonly label: string;
  readonly subscript?: string;
  readonly accent?: string;
  /** When true, render with a dashed border to mark "candidate" / "to be constructed". */
  readonly candidate?: boolean;
}

/** A labeled object: small filled disk with a typeset label next to it. */
export function ObjectNode({
  cx,
  cy,
  label,
  subscript,
  accent = STROKE,
  candidate = false,
}: ObjectNodeProps): React.JSX.Element {
  return (
    <g>
      <circle
        cx={cx}
        cy={cy}
        r={4}
        fill={accent}
        stroke={accent}
        strokeWidth={candidate ? 1.5 : 1}
        strokeDasharray={candidate ? "2 2" : undefined}
      />
      <text
        x={cx}
        y={cy - 10}
        textAnchor="middle"
        fontSize="14"
        fontFamily={DIAGRAM_MONO}
        fontWeight={600}
        fill={LABEL}
      >
        {label}
        {subscript !== undefined && (
          <tspan fontSize="10" dy="3">
            {subscript}
          </tspan>
        )}
      </text>
    </g>
  );
}

export interface ArrowProps {
  readonly x1: number;
  readonly y1: number;
  readonly x2: number;
  readonly y2: number;
  /** Label rendered roughly midway above the arrow. */
  readonly label?: string;
  /** Optional sub-label rendered below the line. */
  readonly subLabel?: string;
  readonly markerId: string;
  readonly color?: string;
  /** "solid" | "dashed" — solid is the default for an existing morphism. */
  readonly variant?: "solid" | "dashed";
  /** Render the arrow with a forbidden ✗ stamp on top. */
  readonly forbidden?: boolean;
  /** Curve the arrow upward (positive) or downward (negative) by this px offset. */
  readonly curve?: number;
}

/** A single labeled arrow with optional curve and "forbidden" stamp. */
export function Arrow({
  x1,
  y1,
  x2,
  y2,
  label,
  subLabel,
  markerId,
  color = STROKE,
  variant = "solid",
  forbidden = false,
  curve = 0,
}: ArrowProps): React.JSX.Element {
  // Midpoint of the straight segment between (x1,y1) and (x2,y2).
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  // The Bézier control point sits `curve` pixels above the midpoint
  // (positive `curve` means "bow upward"). The arc midpoint lies halfway
  // between the chord midpoint and the control point, i.e. `curve / 2`
  // above the chord midpoint — that's where the label belongs.
  const arcMy = my - curve / 2;
  const path =
    curve === 0 ? `M ${x1} ${y1} L ${x2} ${y2}` : `M ${x1} ${y1} Q ${mx} ${my - curve} ${x2} ${y2}`;
  return (
    <g>
      <path
        d={path}
        fill="none"
        stroke={forbidden ? GRAY[500] : color}
        strokeWidth={1.5}
        strokeDasharray={variant === "dashed" || forbidden ? "5 4" : undefined}
        markerEnd={`url(#${markerId}-head)`}
      />
      {label !== undefined && (
        <text
          x={mx}
          y={arcMy - 6}
          textAnchor="middle"
          fontSize="12"
          fontFamily={DIAGRAM_MONO}
          fill={LABEL}
        >
          {label}
        </text>
      )}
      {subLabel !== undefined && (
        <text x={mx} y={arcMy + 14} textAnchor="middle" fontSize="10" fill={GRAY[600]}>
          {subLabel}
        </text>
      )}
      {forbidden && (
        <text
          x={mx}
          y={arcMy + 4}
          textAnchor="middle"
          fontSize="18"
          fontWeight={700}
          fill={GRAY[700]}
        >
          ✗
        </text>
      )}
    </g>
  );
}

/** A small "F" wrapper drawn around an object label to suggest `F(A)`. */
export function FunctorBox({
  cx,
  cy,
  label,
  functor = "F",
  accent = STROKE,
}: {
  readonly cx: number;
  readonly cy: number;
  readonly label: string;
  readonly functor?: string;
  readonly accent?: string;
}): React.JSX.Element {
  const text = `${functor}(${label})`;
  return (
    <g>
      <rect
        x={cx - 24}
        y={cy - 12}
        width={48}
        height={22}
        rx={5}
        ry={5}
        fill="white"
        stroke={accent}
        strokeWidth={1}
      />
      <text
        x={cx}
        y={cy + 3}
        textAnchor="middle"
        fontSize="13"
        fontFamily={DIAGRAM_MONO}
        fill={LABEL}
      >
        {text}
      </text>
    </g>
  );
}

/** Warning red for forbidden / non-existent morphisms. */
export const WARN_COLOR = "#C02323";
